<template>
  <v-row class="match-height faqform">
    <v-col cols="12">
      <v-card>
        <v-card-title>{{ $t("Kysymykset & vastaukset") }}</v-card-title>
        <v-card-text>
          <v-form v-on:submit.prevent="submit" ref="form">
            <v-card outlined elevation="0" class="mb-4">
              <v-tabs v-model="tab" grow>
                <v-tab v-for="lang in tenant.languages" :key="'langtab-' + lang.code">
                  <img :src="lang.icon" style="max-height: 20px" />
                  &nbsp;&nbsp;{{ lang.name }}
                  &nbsp;
                  <v-icon v-if="langOk(faq.translations, lang.code, [
          'question',
          'answer',
        ])
          " color="success">
                    {{ icons.mdiCheck }}
                  </v-icon>
                  <v-icon v-else color="error">
                    {{ icons.mdiBlockHelper }}
                  </v-icon>
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="tab">
                <v-tab-item v-for="lang in tenant.languages" :key="'langtabitem-' + lang.code">
                  <div style="padding: 20px 20px 0 20px">
                    <img :src="lang.icon" style="max-height: 14px" />
                    <br />
                    <br />

                    <v-text-field :id="'faq-title-' + lang.code" v-model="faq.translations[lang.code].question"
                      :label="$t('Kysymys')" outlined class="mb-4" hide-details="auto" />
                    <div>
                      <quill-editor height="400" ref="myQuillEditor" v-model="faq.translations[lang.code].answer"
                        :options="editorOption" />
                    </div>
                  </div>
                </v-tab-item>
              </v-tabs-items>

              <v-input class="ml-4 mb-4" hidden hide-details="auto" :rules="[validateLangs]"></v-input>
            </v-card>

            <v-switch v-model="faq.published" :label="$t('Julkaise')" class="mb-5" persistent-hint :hint="faq.published
          ? $t('Kysymys julkaistaan heti')
          : $t('Kysymys tallennetaan luonnoksena')
          "></v-switch>

            <v-alert color="error" text v-if="!formIsValid">
              {{ $t("Tarkista lomakkeen tiedot") }}
            </v-alert>

            <v-btn v-if="faq.id" color="error" class="mr-3" type="button" @click="removeFaq">
              {{ $t("Poista") }}
            </v-btn>

            <v-btn color="primary" type="submit">
              {{ $t("Tallenna") }}
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { onMounted, reactive, ref } from "@vue/composition-api";
import adminApi from "@/api/admin";
import { useRouter } from "@/@core/utils";
import useAuth from "@/useAuth";
import { useTranslations } from "@/@core/libs/i18n";

import { mdiCheck, mdiBlockHelper } from "@mdi/js";
import {
  convertTranslationMarkdownToHtml,
  convertTranslationsHtmlToMarkdown,
  langOk,
} from "@/@core/utils/translation_helpers";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";
import { quillEditor } from "vue-quill-editor";
import { i18n } from "@/plugins/i18n";
import ImageUploader from 'quill-image-uploader';
import Quill from 'quill';
import 'quill-paste-smart';
Quill.register("modules/imageUploader", ImageUploader);

export default {
  components: {
    quillEditor,
  },
  props: ["id"],
  setup(props) {
    const { router } = useRouter();
    const { tenant, user } = useAuth();
    const formIsValid = ref(true);
    const tab = ref(null);
    const form = ref(null);
    const editorOption = {
      modules: {
        clipboard: {
          keepSelection: true,
          substituteBlockElements: false,
          magicPasteLinks: true,
        },
        toolbar: [
          ["bold", "italic"],
          ["link", "image"],
        ],
        imageUploader: {
          upload: (file) => {
            return new Promise(async (resolve, reject) => {

              const res = await adminApi.uploadImage(file);

              if (res.ok) {
                resolve(res.data);
              }
              else {
                reject("Image upload failed");
              }
            });
          },
        },
      },
    };

    const faq = reactive({
      id: null,
      translations: {},
      published: true,
    });

    tenant.value.languages.forEach((lang) => {
      faq.translations[lang.code] = {
        question: "",
        answer: "",
      };
    });

    const fetchFaq = async (id) => {
      const response = await adminApi.getFaq(id);
      if (response.ok) {
        faq.id = response.data.id;
        faq.published = response.data.published;
        faq.translations = {};
        tenant.value.languages.forEach((lang) => {
          if (response.data.translations[lang.code]) {
            faq.translations[lang.code] = response.data.translations[lang.code];
          } else {
            faq.translations[lang.code] = {
              question: "",
              answer: "",
            };
          }
        });
        convertTranslationMarkdownToHtml(faq.translations, ["answer"]);
      } else {
        alert(response.error);
      }
    };

    const submit = async () => {
      formIsValid.value = true;
      if (form.value.validate()) {
        const saveFaq = Object.assign({}, faq);

        saveFaq.translations = {};

        tenant.value.languages.forEach((lang) => {
          if (langOk(faq.translations, lang.code, ["question", "answer"])) {
            saveFaq.translations[lang.code] = faq.translations[lang.code];
          }
        });
        convertTranslationsHtmlToMarkdown(saveFaq.translations, ["answer"]);

        const response = await adminApi.saveFaq(saveFaq);

        if (response.ok) {
          router.push({ name: "admin-faq-list" });
        } else {
          alert(response.error);
        }
      } else {
        formIsValid.value = false;
      }
    };

    const removeFaq = async () => {
      if (confirm(i18n.t("Haluatko varmasti poistaa kysymyksen?"))) {
        const response = await adminApi.deleteFaq(faq.id);
        if (response.ok) {
          router.replace({ name: "admin-faq-list" });
        } else {
          alert(response.error);
        }
      }
    };

    const validateLangs = () => {
      let valid = false;
      tenant.value.languages.forEach((lang) => {
        if (langOk(faq.translations, lang.code, ["question", "answer"]))
          valid = true;
      });

      if (valid) {
        return true;
      } else {
        return i18n.t("Kysymys tai vastaus puuttuu");
      }
    };

    onMounted(() => {
      if (props.id) {
        fetchFaq(props.id);
      }
    });

    return {
      faq,
      tenant,
      user,
      removeFaq,
      submit,
      langOk,
      validateLangs,
      formIsValid,
      tab,
      form,
      editorOption,
      icons: {
        mdiCheck,
        mdiBlockHelper,
      },
    };
  },
};
</script>



<style>
.faqform .ql-editor p {
  font-size: 1.2em;
  line-height: 1.8em;
}

.faqform .ql-editor {
  min-height: 200px;
}
</style>
